import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import translations from './myorganization.psa.i18n';
import { Grid } from '@mui/material';
import * as css from './departments.psa.module.scss';
import {
  PraksisstedUndernodeDto,
  PraksisstedUndernodeDtoStatusDtoEnum,
} from '../api/generated/praksissted-generated-api';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import { useLoader } from '../api/useLoader';
import Spinner from '../components/spinner/spinner';
import Icon, { IconType } from '../components/icon';
import {
  OnStedHoverCard,
  StedType,
} from '../components/hierarchy/onhoverhierarchyinfo';
import { FlexSpacer } from '../components/spacer/spacer';

const getExpandedStateFromSessionStorage = (topId: string): string[] =>
  JSON.parse(
    sessionStorage.getItem(`expanded-PS-departments-${topId}`) || '[]',
  );

const putExpandedStateInSessionStorage = (topId: string, expanded: string[]) =>
  sessionStorage.setItem(
    `expanded-PS-departments-${topId}`,
    JSON.stringify(expanded),
  );

const Departments = () => {
  const { formatMessage } = useIntl();
  const { praksisstedApi } = useContext(PraksisstedApiContext);
  const { data } = useLoader(() => praksisstedApi.avdelingerGet(), []);

  const avdelingsoversiktInfo = useMemo(
    () =>
      formatMessage({
        id: translations.avdelingsoversiktInfo.id,
      }),
    [formatMessage],
  );

  return data ? (
    <div className={css.avdelinger}>
      <p>{avdelingsoversiktInfo}</p>
      <Grid container className={classNames(css.table, 'avdelingerTable')}>
        <Grid item xs={8} className={css.headerRow}>
          Praksissteder
        </Grid>
        <Grid item xs={4} className={css.headerRow}>
          Status
        </Grid>
        <Avdeling
          avdeling={{ ...data, under: [] }}
          indent={1}
          removeEndIcon
          className={classNames(
            css.organizationTop,
            css.row,
            css.topRow,
            'top-level-praksissted',
          )}
          topId={data.id}
        />

        {data.under.map((avdeling) => (
          <Avdeling
            avdeling={avdeling}
            indent={3} // Skip one, as the toggle button has negative margin
            key={avdeling.id}
            topId={data.id}
            className={classNames(css.row, css.topRow, 'top-level-praksissted')}
            removeEndIcon
          />
        ))}
      </Grid>
    </div>
  ) : (
    <Spinner />
  );
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.substring(1);

interface AvdelingProps {
  avdeling: PraksisstedUndernodeDto;
  indent: number;
  showHelp?: boolean;
  removeEndIcon?: boolean;
  className?: string;
  topId: string;
}

const Avdeling: React.FC<React.PropsWithChildren<AvdelingProps>> = ({
  avdeling,
  indent,
  removeEndIcon,
  className,
  topId,
}) => {
  const { formatMessage } = useIntl();
  const { id } = avdeling;

  const [isExpanded, setIsExpanded] = useState(
    getExpandedStateFromSessionStorage(topId).includes(id)!!,
  );

  const translateStatus = useCallback(
    (innerStatus: PraksisstedUndernodeDtoStatusDtoEnum) =>
      formatMessage({
        id: translations[`avdelingStatus${capitalize(innerStatus.toString())}`]
          .id,
      }),
    [formatMessage],
  );

  const status = useMemo(
    () => translateStatus(avdeling.status!),
    [translateStatus, avdeling.status],
  );

  const undernodeIdsAndSelf = useMemo(
    () => flattenUndernodeIdsAndSelf(avdeling),
    [avdeling],
  );

  const expandedHandler = () => {
    const newExpandedState = !isExpanded;

    // Must be loaded for each expand/collapse action
    // as multiple subcomponents access the same value
    const expandedIds = getExpandedStateFromSessionStorage(topId);

    setIsExpanded(newExpandedState);
    putExpandedStateInSessionStorage(
      topId,
      newExpandedState
        ? expandedIds.concat(id)
        : expandedIds.filter(
            (expandedId) => !undernodeIdsAndSelf.includes(expandedId),
          ),
    );
  };

  return (
    <>
      <Grid container onClick={expandedHandler} className={className}>
        <Grid item xs={8}>
          <FlexSpacer width={indent}>
            <div className={css.iconAndName}>
              <RowIcon
                isExpanded={isExpanded}
                avdeling={avdeling}
                removeEndIcon={removeEndIcon}
              />
              <AvdelingName avdeling={avdeling} />
            </div>
          </FlexSpacer>
        </Grid>
        <Grid item xs={4}>
          {status}
        </Grid>
      </Grid>
      {isExpanded &&
        avdeling.under.map((avd) => (
          <Avdeling
            avdeling={avd}
            indent={indent + 1}
            key={avd.id}
            topId={topId}
            className={css.row}
          />
        ))}
    </>
  );
};

const flattenUndernodeIdsAndSelf = (
  avdeling: PraksisstedUndernodeDto,
): string[] =>
  [avdeling.id].concat(
    avdeling.under.flatMap((sted) =>
      [sted.id].concat(flattenUndernodeIdsAndSelf(sted)),
    ),
  );

interface ExpandCollapseProps {
  isExpanded: boolean;
  avdeling: PraksisstedUndernodeDto;
  removeEndIcon?: boolean;
}

const RowIcon: React.FC<React.PropsWithChildren<ExpandCollapseProps>> = ({
  avdeling,
  isExpanded,
  removeEndIcon,
}) => {
  const icon = useMemo(() => {
    const hasChildrenNodes = avdeling.under.length > 0;

    if (hasChildrenNodes) {
      return isExpanded ? (
        <Icon type={IconType.CloseRowTriangle} htmlId={avdeling.id} />
      ) : (
        <Icon type={IconType.OpenRowTriangle} htmlId={avdeling.id} />
      );
    }

    return (
      !removeEndIcon &&
      avdeling.under.length === 0 && (
        <div className={css.endNodeWrapper}>
          <Icon type={IconType.EndNode} htmlId={avdeling.id} />
        </div>
      )
    );
  }, [avdeling, isExpanded, removeEndIcon]);

  return icon ? <div className={css.iconWrapper}>{icon}</div> : <></>;
};

interface AvdelingNameProps {
  avdeling: PraksisstedUndernodeDto;
  showHelp?: boolean;
}

const AvdelingName: React.FC<React.PropsWithChildren<AvdelingNameProps>> = ({
  avdeling,
  showHelp,
}) => (
  <div className={css.name}>
    <Link
      to={`/myorganization/practiceplaces/profile/${avdeling.id}`}
      data-cy={'link'}
    >
      {avdeling.navn}
    </Link>
    {showHelp && (
      <OnStedHoverCard
        id={avdeling.id}
        navn={avdeling.navn}
        sted={StedType.PRAKSISSTED}
      />
    )}
  </div>
);

export default Departments;
